import useStyles from 'isomorphic-style-loader/useStyles';
import { CircleAlert } from 'lucide-react';
import { useWatch, type Control } from 'react-hook-form';
import { Form, Message } from 'semantic-ui-react';

import {
  VULNERABILITY_ANNOTATION_ACTION_STATEMENT_MAX_LENGTH,
  VULNERABILITY_ANNOTATION_ADDITIONAL_DETAILS_MAX_LENGTH,
  VULNERABILITY_ANNOTATION_IMPACT_STATEMENT_MAX_LENGTH,
  VULNERABILITY_ANNOTATION_STATUS_NOTES_MAX_LENGTH,
} from '@models/autogen/v2.constraints';
import { Field } from '@components/form/fields';

import type { VulnerabilityAnnotationFormValues } from './vulnerability-annotation-form.schema';
import {
  VEX_JUSTIFICATIONS,
  VEX_STATUS_OPTIONS,
} from './vulnerability-annotation.const';
import s from './vulnerability-annotation-form.scss';

export interface VulnerabilityAnnotationFormProps {
  control: Control<VulnerabilityAnnotationFormValues>;
  /**
   * Disable every field, including Status (e.g. for a loading state).
   */
  disabled?: boolean;
  /**
   * Whether the form is editing an existing annotation (vs. creating a
   * new one). Drives the warning shown when Status is cleared to None.
   */
  isEditing?: boolean;
}

export const VulnerabilityAnnotationForm = ({
  control,
  disabled = false,
  isEditing = false,
}: VulnerabilityAnnotationFormProps) => {
  useStyles(s);

  const status = useWatch({ control, name: 'status' });
  const isDisabled = disabled || status === null;
  const showDeleteWarning = isEditing && !disabled && status === null;

  return (
    <Form className={s.form} warning={showDeleteWarning}>
      <Field.Dropdown
        control={control}
        disabled={disabled}
        label="Status"
        name="status"
        options={VEX_STATUS_OPTIONS}
        placeholder="None"
        floating
        required
      />

      {showDeleteWarning && (
        <Message className={s.deleteWarning} warning>
          <CircleAlert className={s.deleteWarningIcon} />
          Deleting will clear all fields below. This cannot be undone.
        </Message>
      )}

      <Field.Textarea
        control={control}
        disabled={isDisabled}
        label="Status Notes"
        limit={VULNERABILITY_ANNOTATION_STATUS_NOTES_MAX_LENGTH}
        name="statusNotes"
        placeholder="Explain the status value with additional details."
      />

      <Field.Dropdown
        control={control}
        disabled={isDisabled}
        label="Justification"
        name="justification"
        options={VEX_JUSTIFICATIONS}
        placeholder="Select justification"
        clearable
        floating
        nullable
      />

      <Field.Textarea
        control={control}
        disabled={isDisabled}
        label="Impact Statement"
        limit={VULNERABILITY_ANNOTATION_IMPACT_STATEMENT_MAX_LENGTH}
        name="impactStatement"
        placeholder="Explain the status value if the justification is not provided."
      />

      <Field.Textarea
        control={control}
        disabled={isDisabled}
        label="Action Statement"
        limit={VULNERABILITY_ANNOTATION_ACTION_STATEMENT_MAX_LENGTH}
        name="actionStatement"
        placeholder="Describe actions being taken in response."
      />

      <Field.Textarea
        control={control}
        disabled={isDisabled}
        label="Additional Details"
        limit={VULNERABILITY_ANNOTATION_ADDITIONAL_DETAILS_MAX_LENGTH}
        name="additionalDetails"
        placeholder="Enter any additional information regarding the exploitability statement for this security vulnerability."
      />
    </Form>
  );
};

export default VulnerabilityAnnotationForm;
